/**
 * Builds one recommended solar design from the intake configuration.
 * Fast path: max-fill configured orientation, heuristics on full module pool + one simulation.
 */
import { callGemini, GEMINI_MODEL_ID } from '../../services/geminiService';
import type { SavedRoofModulesLayout } from '../../@types/types';
import {
    collectIncludedModuleIds,
    requestPciMaxfillStrategiesFromEmbed,
    requestPciSimulateOptionsFromEmbed,
    type PciMaxfillSnapshot,
    type PciSimulatedOptionResult,
} from './preContractEmbedBridge';
import {
    countIncludedModules,
    estimateLayoutAnnualKwh,
    isPreContractGreyPanel,
    isPreContractPanelAllowed,
    isPreContractLowEfficiency,
    isPreContractPurplePanel,
    PRE_CONTRACT_GREY_SA_MIN,
    PRE_CONTRACT_LOW_EFFICIENCY_SA,
    PRE_CONTRACT_PURPLE_SA_MAX,
} from './preContractModuleLayout';
import type {
    PreContractFillOrientation,
    PreContractIntakeForm,
    PreContractModuleCell,
    PreContractRoofModuleLayout,
    PreContractSiteTree,
} from './preContractTypes';
import { normalizeFillOrientations } from './preContractTypes';
import treeCartService, {
    getTreeCalculatorBandsForTree,
    getTreeCutCostWithDefaults,
} from '../../services/treeCartService';

const HIGH_ACCESS_SA = 75;
const RELAXED_ACCESS_SA = 65;
/** Single recommended design for pre-contract intake (fast path). */
const MAX_OPTIONS = 2;
const GEMINI_DESIGN_TIMEOUT_MS = 25000;
const SPECIFIC_YIELD = 1250;

export type PreContractAiProgress = 'layout' | 'maxfill' | 'gemini' | 'simulate' | 'treecut';

/** Tree-cut extras attached to an AI option on the Tree Cutting results tab. */
export type PreContractAiTreeCut = {
    treeIds: string[];
    treeLabels: string[];
    cutCost: number | null;
    annualKwhGain: number | null;
    /** Tree Calculator DBH dropdown from the cut tree trunk. */
    dbhRange?: string | null;
    /** Tree Calculator height dropdown from the cut tree mesh. */
    heightRange?: string | null;
};

/** One AI-ranked layout the sales user can pick after Run AI. */
export type PreContractAiDesignOption = {
    id: string;
    rank: number;
    title: string;
    reason: string;
    layouts: PreContractRoofModuleLayout[];
    roofSelections: boolean[];
    moduleCount: number;
    systemKw: number;
    annualKwh: number;
    /** Utility production (pre-loss) when measured in OneDraw, else null. */
    utilityKwh?: number | null;
    averageSolarAccess: number;
    /** Efficiency (TSRF) from the live Production calc when simulated. */
    efficiencyTsrf?: number | null;
    /** System multiplier from the live Production calc or included-module average. */
    multiplier?: number | null;
    offsetPercent: number | null;
    roofLabels: string[];
    includesGrayPanels: boolean;
    includesGreyPanels?: boolean;
    includesPurplePanels?: boolean;
    /** Portrait / landscape / mixed max-fill this option was taken from. */
    fillOrientation?: PreContractFillOrientation;
    /** Exact 3D placement captured while simulating this option in the embed. */
    modulesLayout?: SavedRoofModulesLayout;
    /** Trees that should be ghosted when this option is selected. */
    ghostedTreeIds?: string[];
    /** Present on Tree Cutting tab options. */
    treeCut?: PreContractAiTreeCut;
};

export type PreContractAiDesignsResult = {
    layoutOptions: PreContractAiDesignOption[];
    treeCutOptions: PreContractAiDesignOption[];
};

type CandidateStrategy =
    | 'your-layout'
    | 'max-production'
    | 'usage-fit'
    | 'high-access'
    | 'best-roofs';

type RankedCandidate = PreContractAiDesignOption & {
    strategy: CandidateStrategy;
    score: number;
};

type GeminiPick = {
    id: string;
    title?: string;
    reason?: string;
    moduleIds?: string[];
    orientation?: PreContractFillOrientation;
};

function parseUsageKwh(raw: string): number {
    const n = Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Annual usage from intake — the kWh the ranked layouts are trying to reach. */
export function parsePreContractTargetKwh(raw: string): number {
    return parseUsageKwh(raw);
}

function cloneLayouts(layouts: PreContractRoofModuleLayout[]): PreContractRoofModuleLayout[] {
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => (cell ? { ...cell } : null)),
    }));
}

function isGreyPanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractGreyPanel(cell.solarAccess);
}

function isPurplePanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractPurplePanel(cell.solarAccess);
}

function isGrayPanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractLowEfficiency(cell.solarAccess);
}

function collectPresentCells(
    layouts: PreContractRoofModuleLayout[],
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    const out: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> = [];
    layouts.forEach((layout) => {
        layout.modules.forEach((cell) => {
            if (cell) out.push({ roofIndex: layout.roofIndex, cell });
        });
    });
    return out;
}

function withIncludedIds(
    layouts: PreContractRoofModuleLayout[],
    includedIds: Set<string>,
    baseRoofSelections?: boolean[],
): { layouts: PreContractRoofModuleLayout[]; roofSelections: boolean[] } {
    const next = cloneLayouts(layouts).map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            return { ...cell, included: includedIds.has(cell.id) };
        }),
    }));
    // AI designs never keep a roof with fewer than 2 included modules.
    const MIN_MODULES_PER_ROOF = 2;
    next.forEach((layout) => {
        const includedCount = layout.modules.filter((cell) => cell?.included).length;
        if (includedCount > 0 && includedCount < MIN_MODULES_PER_ROOF) {
            layout.modules = layout.modules.map((cell) =>
                cell ? { ...cell, included: false } : null,
            );
        }
    });
    const roofSelections = next.map((layout) => {
        if (baseRoofSelections && baseRoofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => Boolean(cell?.included));
    });
    return { layouts: next, roofSelections };
}

function applySolarAccessByModuleId(
    layouts: PreContractRoofModuleLayout[],
    saById: Record<string, number> | undefined,
): PreContractRoofModuleLayout[] {
    if (!saById) return layouts;
    const upper: Record<string, number> = {};
    Object.entries(saById).forEach(([key, value]) => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            upper[key.trim().toUpperCase()] = value;
        }
    });
    if (Object.keys(upper).length === 0) return layouts;
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            const sa = upper[String(cell.id).trim().toUpperCase()];
            return sa != null ? { ...cell, solarAccess: sa } : cell;
        }),
    }));
}

function applySimulatedMetrics(
    candidate: RankedCandidate,
    sim: PciSimulatedOptionResult | undefined,
    form: PreContractIntakeForm,
): RankedCandidate {
    if (!sim) return candidate;
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const layouts = applySolarAccessByModuleId(candidate.layouts, sim.solarAccessByModuleId);
    const saFromMeshes = Object.values(sim.solarAccessByModuleId ?? {});
    const meshSaAvg =
        saFromMeshes.length > 0
            ? saFromMeshes.reduce((sum, value) => sum + value, 0) / saFromMeshes.length
            : 0;
    const annualKwh = sim.annualKwh > 0 ? sim.annualKwh : candidate.annualKwh;
    const next: RankedCandidate = {
        ...candidate,
        layouts,
        annualKwh,
        utilityKwh: sim.utilityKwh > 0 ? sim.utilityKwh : candidate.utilityKwh ?? null,
        averageSolarAccess:
            sim.averageSolarAccess > 0
                ? sim.averageSolarAccess
                : meshSaAvg > 0
                  ? meshSaAvg
                  : candidate.averageSolarAccess,
        efficiencyTsrf:
            sim.efficiency != null && sim.efficiency > 0
                ? sim.efficiency
                : candidate.efficiencyTsrf ?? null,
        multiplier:
            sim.multiplier != null && sim.multiplier > 0
                ? sim.multiplier
                : candidate.multiplier ?? null,
        moduleCount: sim.moduleCount > 0 ? sim.moduleCount : candidate.moduleCount,
        systemKw: sim.systemKw > 0 ? sim.systemKw : candidate.systemKw,
        offsetPercent: usageKwh > 0 ? Math.round((annualKwh / usageKwh) * 100) : null,
        // Prefer the simulated included placement over the full max-fill snapshot.
        modulesLayout: sim.modulesLayout?.length
            ? sim.modulesLayout
            : candidate.modulesLayout,
    };
    return { ...next, score: scoreCandidate(next, form) };
}

async function simulateCandidatesInEmbed(
    candidates: RankedCandidate[],
    form: PreContractIntakeForm,
    fills?: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>>,
    ghostedTreeIds?: string[],
): Promise<RankedCandidate[]> {
    const jobs = candidates.map((candidate) => ({
        id: candidate.id,
        includedModuleIds: collectIncludedModuleIds(candidate.layouts),
        fillOrientation: candidate.fillOrientation,
    }));
    const started = Date.now();
    const results = await requestPciSimulateOptionsFromEmbed(jobs, fills, ghostedTreeIds);
    if (results?.length) {
        const byId = new Map(results.map((row) => [row.id, row]));
        const next = candidates.map((candidate) =>
            applySimulatedMetrics(candidate, byId.get(candidate.id), form),
        );
        next.sort((a, b) => b.score - a.score);
        return next;
    }
    if (Date.now() - started > 20000) return candidates;
    await new Promise((resolve) => window.setTimeout(resolve, 1200));
    const retry = await requestPciSimulateOptionsFromEmbed(jobs, fills, ghostedTreeIds);
    if (!retry?.length) return candidates;
    const byId = new Map(retry.map((row) => [row.id, row]));
    const next = candidates.map((candidate) =>
        applySimulatedMetrics(candidate, byId.get(candidate.id), form),
    );
    next.sort((a, b) => b.score - a.score);
    return next;
}

function layoutSignature(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    fillOrientation?: PreContractFillOrientation,
): string {
    const ids: string[] = [];
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) ids.push(cell.id);
        });
    });
    ids.sort();
    return `${fillOrientation ?? ''}|${ids.join('|')}`;
}

function systemKwFromCount(moduleCount: number, wattage: number | null): number {
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0 || moduleCount <= 0) return 0;
    return Math.round(((moduleCount * wattage) / 1000) * 1000) / 1000;
}

/** Average production multiplier across included modules (Production panel Multiplier). */
function averageIncludedMultiplier(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number | null {
    let sum = 0;
    let count = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const multiplier = Number(cell.multiplier);
            if (!Number.isFinite(multiplier) || multiplier <= 0) return;
            sum += multiplier;
            count += 1;
        });
    });
    if (count === 0) return null;
    return Math.round((sum / count) * 1000) / 1000;
}

function kwhPerModule(wattage: number | null): number {
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return 0;
    return (wattage / 1000) * SPECIFIC_YIELD;
}

function poolCells(
    layouts: PreContractRoofModuleLayout[],
    form: PreContractIntakeForm,
    roofSelections?: boolean[],
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    return collectPresentCells(layouts).filter(({ roofIndex, cell }) => {
        if (roofSelections && roofSelections[roofIndex] === false) return false;
        return isPreContractPanelAllowed(
            cell.solarAccess,
            form.includeGreyPanels,
            form.includePurplePanels,
        );
    });
}

/** @deprecated Use poolCells with form — kept as alias for callers that expect the old name. */
function eligibleCells(
    layouts: PreContractRoofModuleLayout[],
    allowGray: boolean,
    roofSelections?: boolean[],
    form?: PreContractIntakeForm,
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    if (form) return poolCells(layouts, form, roofSelections);
    const includeGrey = allowGray;
    const includePurple = allowGray;
    return collectPresentCells(layouts).filter(({ roofIndex, cell }) => {
        if (roofSelections && roofSelections[roofIndex] === false) return false;
        return isPreContractPanelAllowed(cell.solarAccess, includeGrey, includePurple);
    });
}

function buildOption(
    id: string,
    strategy: CandidateStrategy,
    title: string,
    reason: string,
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
    score: number,
): RankedCandidate | null {
    const { count, averageSolarAccess } = countIncludedModules(layouts, roofSelections);
    if (count <= 0) return null;

    const annualKwh =
        estimateLayoutAnnualKwh({
            layouts,
            roofSelections,
            moduleWattage: form.moduleTypeWattage,
        }) ?? 0;
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const includesGreyPanels = layouts.some((layout) => {
        if (roofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => cell?.included && isGreyPanel(cell));
    });
    const includesPurplePanels = layouts.some((layout) => {
        if (roofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => cell?.included && isPurplePanel(cell));
    });
    const includesGrayPanels = includesGreyPanels || includesPurplePanels;
    const roofLabels = layouts
        .filter((layout) => roofSelections[layout.roofIndex] !== false)
        .filter((layout) => layout.modules.some((cell) => cell?.included))
        .map((layout) => layout.label);

    return {
        id,
        rank: 0,
        title,
        reason,
        strategy,
        score,
        layouts,
        roofSelections,
        moduleCount: count,
        systemKw: systemKwFromCount(count, form.moduleTypeWattage),
        annualKwh,
        utilityKwh: null,
        averageSolarAccess,
        efficiencyTsrf: null,
        multiplier: averageIncludedMultiplier(layouts, roofSelections),
        offsetPercent: usageKwh > 0 ? Math.round((annualKwh / usageKwh) * 100) : null,
        roofLabels,
        includesGrayPanels,
        includesGreyPanels,
        includesPurplePanels,
    };
}

function scoreCandidate(candidate: RankedCandidate, form: PreContractIntakeForm): number {
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const offset = usageKwh > 0 ? candidate.annualKwh / usageKwh : 1;
    const balanced = form.offsetPreference === 'balanced';
    let offsetScore: number;

    if (form.offsetPreference === 'full' && usageKwh > 0) {
        // Covering usage always ranks above undershoot; modest overshoot is fine.
        offsetScore =
            offset >= 0.98
                ? 1 - Math.min(0.18, Math.max(0, offset - 1.25) * 0.4)
                : Math.max(0, offset) * 0.7;
    } else {
        offsetScore = 1 - Math.min(1, Math.abs(offset - 1));
        if (balanced && offset > 1.2) offsetScore -= 0.2;
        if (balanced && offset < 0.7) offsetScore -= 0.18;
    }

    const saScore = Math.max(0, Math.min(1, candidate.averageSolarAccess / 100));
    let shadedPenalty = 0;
    if (!form.includeGreyPanels && candidate.includesGreyPanels) shadedPenalty += 0.18;
    if (!form.includePurplePanels && candidate.includesPurplePanels) shadedPenalty += 0.22;
    if (form.includeGreyPanels && !candidate.includesGreyPanels && offset < 0.85) {
        shadedPenalty += 0.04;
    }
    if (form.includePurplePanels && !candidate.includesPurplePanels && offset < 0.85) {
        shadedPenalty += 0.04;
    }
    if (balanced && candidate.includesGrayPanels && offset >= 0.98) shadedPenalty += 0.12;

    const productionBonus =
        form.offsetPreference === 'full' && usageKwh > 0
            ? Math.min(1.15, candidate.annualKwh / usageKwh) * 0.12
            : 0;

    const offsetWeight = balanced ? 0.5 : 0.55;
    const saWeight = balanced ? 0.4 : 0.33;
    return offsetScore * offsetWeight + saScore * saWeight + productionBonus - shadedPenalty;
}

function defaultCopy(
    strategy: CandidateStrategy,
    form: PreContractIntakeForm,
): { title: string; reason: string } {
    const full = form.offsetPreference === 'full';
    const skipGrey = !form.includeGreyPanels;
    const skipPurple = !form.includePurplePanels;
    switch (strategy) {
        case 'your-layout':
            return {
                title: 'Your current layout',
                reason: 'Keeps the roofs and modules you already included on the previous screen.',
            };
        case 'max-production':
            return {
                title: full ? 'Full-offset max production' : 'Highest production',
                reason:
                    skipGrey && skipPurple
                        ? 'Fills eligible roofs while skipping grey and purple panels.'
                        : skipGrey
                          ? 'Fills eligible roofs while skipping grey (30–50% SA) panels.'
                          : skipPurple
                            ? 'Fills eligible roofs while skipping purple (<30% SA) panels.'
                            : 'Places as many modules as possible to maximize annual production.',
            };
        case 'usage-fit':
            return {
                title: full ? 'Covers annual usage' : 'Balanced usage fit',
                reason: full
                    ? 'Adds the strongest panels first until production covers annual utility usage.'
                    : 'Uses the strongest solar-access modules to get as close as possible to annual usage.',
            };
        case 'high-access':
            return {
                title: 'Best solar access',
                reason: 'Keeps only high solar-access modules so the array stays efficient and cleaner.',
            };
        case 'best-roofs':
            return {
                title: 'Best roofs only',
                reason: 'Concentrates modules on the strongest roof faces instead of spreading across the whole house.',
            };
        default:
            return { title: 'Design option', reason: 'Generated from the intake you provided.' };
    }
}

function asStrategy(id: string): CandidateStrategy {
    const key = id.replace(/^(portrait|landscape|both)-/, '');
    if (
        key === 'your-layout' ||
        key === 'max-production' ||
        key === 'usage-fit' ||
        key === 'high-access' ||
        key === 'best-roofs'
    ) {
        return key;
    }
    return 'usage-fit';
}

function asFillOrientation(value: unknown): PreContractFillOrientation | undefined {
    if (value === 'portrait' || value === 'landscape' || value === 'both') return value;
    const text = String(value ?? '').toLowerCase();
    if (text.includes('land')) return 'landscape';
    if (text.includes('mix') || text.includes('both')) return 'both';
    if (text.includes('port')) return 'portrait';
    return undefined;
}

function candidateFromIds(
    id: string,
    includedIds: Set<string>,
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate | null {
    const strategyKey = id.replace(/^(portrait|landscape|both)-/, '');
    const strategy = asStrategy(strategyKey);
    const { layouts, roofSelections } = withIncludedIds(
        form.roofModuleLayouts,
        includedIds,
        form.roofSelections,
    );
    const copy = defaultCopy(strategy, form);
    const optionId = fillOrientation ? `${fillOrientation}-${strategyKey}` : id;
    const draft = buildOption(
        optionId,
        strategy,
        copy.title,
        copy.reason,
        layouts,
        roofSelections,
        form,
        0,
    );
    if (!draft) return null;
    const withOrientation: RankedCandidate = { ...draft, fillOrientation };
    return { ...withOrientation, score: scoreCandidate(withOrientation, form) };
}

function idsForYourLayout(form: PreContractIntakeForm): Set<string> {
    const ids = new Set<string>();
    form.roofModuleLayouts.forEach((layout) => {
        if (form.roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) ids.add(cell.id);
        });
    });
    return ids;
}

function idsForMaxProduction(form: PreContractIntakeForm): Set<string> {
    const ids = new Set<string>();
    poolCells(form.roofModuleLayouts, form, form.roofSelections).forEach(({ cell }) => ids.add(cell.id));
    return ids;
}

function moduleAnnualKwh(
    cell: NonNullable<PreContractModuleCell>,
    wattage: number | null,
): number {
    if (typeof cell.productionKwh === 'number' && cell.productionKwh > 0) {
        return cell.productionKwh;
    }
    const base = kwhPerModule(wattage);
    if (base <= 0) return 0;
    const sa =
        Number.isFinite(cell.solarAccess) && cell.solarAccess > 0
            ? Math.min(cell.solarAccess, 100) / 100
            : 1;
    return base * sa;
}

function includedKwhFromIds(form: PreContractIntakeForm, ids: Set<string>): number {
    let kwh = 0;
    collectPresentCells(form.roofModuleLayouts).forEach(({ roofIndex, cell }) => {
        if (form.roofSelections[roofIndex] === false) return;
        if (!ids.has(cell.id)) return;
        kwh += moduleAnnualKwh(cell, form.moduleTypeWattage);
    });
    return kwh;
}

/** Full offset: keep adding remaining eligible modules until production covers annual usage. */
function expandIdsToCoverUsage(form: PreContractIntakeForm, includedIds: Set<string>): Set<string> {
    const target = parseUsageKwh(form.annualUsageKwh);
    if (target <= 0) return includedIds;
    const remaining = poolCells(form.roofModuleLayouts, form, form.roofSelections)
        .filter(({ cell }) => !includedIds.has(cell.id))
        .sort((a, b) => {
            const kwhDelta =
                moduleAnnualKwh(b.cell, form.moduleTypeWattage) -
                moduleAnnualKwh(a.cell, form.moduleTypeWattage);
            if (kwhDelta !== 0) return kwhDelta;
            return b.cell.solarAccess - a.cell.solarAccess;
        });
    const ids = new Set(includedIds);
    let kwh = includedKwhFromIds(form, ids);
    for (const { cell } of remaining) {
        if (kwh >= target) break;
        ids.add(cell.id);
        kwh += moduleAnnualKwh(cell, form.moduleTypeWattage);
    }
    return ids;
}

function idsForUsageFit(form: PreContractIntakeForm): Set<string> {
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const cells = poolCells(form.roofModuleLayouts, form, form.roofSelections).sort(
        (a, b) => {
            const kwhDelta =
                moduleAnnualKwh(b.cell, form.moduleTypeWattage) -
                moduleAnnualKwh(a.cell, form.moduleTypeWattage);
            if (kwhDelta !== 0) return kwhDelta;
            return b.cell.solarAccess - a.cell.solarAccess;
        },
    );
    if (cells.length === 0) return new Set();
    if (usageKwh <= 0) {
        return new Set(cells.map(({ cell }) => cell.id));
    }

    const target = usageKwh;
    const full = form.offsetPreference === 'full';
    const ids = new Set<string>();
    let kwh = 0;
    for (const { cell } of cells) {
        const add = moduleAnnualKwh(cell, form.moduleTypeWattage);
        const next = kwh + (add > 0 ? add : kwhPerModule(form.moduleTypeWattage));
        if (!full) {
            const closeEnough = kwh >= target * 0.92;
            if (closeEnough && cell.solarAccess < HIGH_ACCESS_SA) break;
            if (ids.size > 0 && Math.abs(next - target) > Math.abs(kwh - target) && next > target) {
                break;
            }
        }
        ids.add(cell.id);
        kwh = next;
        if (kwh >= target) break;
    }
    return ids;
}

function idsForHighAccess(form: PreContractIntakeForm): Set<string> {
    const cells = poolCells(form.roofModuleLayouts, form, form.roofSelections);
    const strict = cells.filter(({ cell }) => cell.solarAccess >= HIGH_ACCESS_SA);
    const pool = strict.length >= 4 ? strict : cells.filter(({ cell }) => cell.solarAccess >= RELAXED_ACCESS_SA);
    const chosen = pool.length > 0 ? pool : cells;
    return new Set(chosen.map(({ cell }) => cell.id));
}

function idsForBestRoofs(form: PreContractIntakeForm): Set<string> {
    const cells = poolCells(form.roofModuleLayouts, form, form.roofSelections);
    if (cells.length === 0) return new Set();

    const byRoof = new Map<number, Array<NonNullable<PreContractModuleCell>>>();
    cells.forEach(({ roofIndex, cell }) => {
        const list = byRoof.get(roofIndex) ?? [];
        list.push(cell);
        byRoof.set(roofIndex, list);
    });

    const rankedRoofs = [...byRoof.entries()]
        .map(([roofIndex, roofCells]) => {
            const avg = roofCells.reduce((sum, cell) => sum + cell.solarAccess, 0) / roofCells.length;
            return { roofIndex, avg, roofCells };
        })
        .sort((a, b) => b.avg - a.avg);

    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const target = usageKwh;
    const full = form.offsetPreference === 'full';
    const ids = new Set<string>();
    let kwh = 0;
    for (const roof of rankedRoofs) {
        const roofKwh = roof.roofCells.reduce(
            (sum, cell) => sum + moduleAnnualKwh(cell, form.moduleTypeWattage),
            0,
        );
        const next = kwh + roofKwh;
        if (
            !full &&
            ids.size > 0 &&
            target > 0 &&
            kwh >= target * 0.85 &&
            Math.abs(next - target) > Math.abs(kwh - target) &&
            next > target
        ) {
            break;
        }
        roof.roofCells.forEach((cell) => {
            ids.add(cell.id);
            kwh += moduleAnnualKwh(cell, form.moduleTypeWattage);
        });
        if (target > 0 && kwh >= target) break;
    }
    return ids;
}

function collectHeuristicCandidates(
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate[] {
    const drafts: Array<RankedCandidate | null> = [
        candidateFromIds('your-layout', idsForYourLayout(form), form, fillOrientation),
        candidateFromIds('max-production', idsForMaxProduction(form), form, fillOrientation),
        candidateFromIds('usage-fit', idsForUsageFit(form), form, fillOrientation),
        candidateFromIds('high-access', idsForHighAccess(form), form, fillOrientation),
        candidateFromIds('best-roofs', idsForBestRoofs(form), form, fillOrientation),
    ];

    const seen = new Set<string>();
    const unique: RankedCandidate[] = [];
    drafts.forEach((draft) => {
        if (!draft) return;
        const sig = layoutSignature(draft.layouts, draft.roofSelections, draft.fillOrientation);
        if (!sig || seen.has(sig)) return;
        seen.add(sig);
        unique.push(draft);
    });

    unique.sort((a, b) => b.score - a.score);
    return unique;
}

function parseGeminiPicks(raw: string): GeminiPick[] {
    const trimmed = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
    try {
        const parsed = JSON.parse(trimmed) as { layouts?: GeminiPick[]; picks?: GeminiPick[] } | GeminiPick[];
        const picks = Array.isArray(parsed) ? parsed : parsed.layouts ?? parsed.picks;
        if (!Array.isArray(picks)) return [];
        return picks
            .filter((pick) => pick && typeof pick.id === 'string' && pick.id.trim())
            .map((pick) => ({
                ...pick,
                orientation: asFillOrientation(pick.orientation ?? (pick as { strategy?: string }).strategy),
            }));
    } catch {
        return [];
    }
}

function cellMatchKeys(cell: { id?: string; meshId?: string }): string[] {
    const keys: string[] = [];
    const id = String(cell.id ?? '').trim().toUpperCase();
    if (id) keys.push(id);
    const meshId = String(cell.meshId ?? '').trim().toUpperCase();
    if (meshId) keys.push(meshId);
    return keys;
}

function knownModuleIdMap(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): Map<string, string> {
    const known = new Map<string, string>();
    layouts.forEach((layout) => {
        if (roofSelections && roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.id || !cell.included) return;
            cellMatchKeys(cell).forEach((key) => known.set(key, cell.id));
        });
    });
    return known;
}

function resolveModuleIds(
    rawIds: string[] | undefined,
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): Set<string> {
    const known = knownModuleIdMap(layouts, roofSelections);
    const ids = new Set<string>();
    (rawIds ?? []).forEach((id) => {
        const match = known.get(String(id).trim().toUpperCase());
        if (match) ids.add(match);
    });
    return ids;
}

function intakeLayoutFor(
    form: PreContractIntakeForm,
    layout: PreContractRoofModuleLayout,
    index: number,
): PreContractRoofModuleLayout | undefined {
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        const match = form.roofModuleLayouts.find((row) => row.roofTagId === layout.roofTagId);
        if (match) return match;
    }
    return (
        form.roofModuleLayouts.find((row) => row.roofIndex === layout.roofIndex) ??
        form.roofModuleLayouts[index]
    );
}

function intakeRoofHasSelectedModules(layout: PreContractRoofModuleLayout | undefined): boolean {
    if (!layout) return true;
    if (!layout.modules.some(Boolean)) return true;
    return layout.modules.some((cell) => Boolean(cell?.included));
}

function roofIsActiveInForm(
    form: PreContractIntakeForm,
    layout: PreContractRoofModuleLayout,
    index: number,
): boolean {
    const intake = intakeLayoutFor(form, layout, index);
    if (!intakeRoofHasSelectedModules(intake)) return false;
    if (intake && typeof form.roofSelections[intake.roofIndex] === 'boolean') {
        return form.roofSelections[intake.roofIndex] !== false;
    }
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        const match = form.roofModuleLayouts.find((row) => row.roofTagId === layout.roofTagId);
        if (match) return form.roofSelections[match.roofIndex] !== false;
    }
    if (typeof form.roofSelections[layout.roofIndex] === 'boolean') {
        return form.roofSelections[layout.roofIndex] !== false;
    }
    return form.roofSelections[index] !== false;
}

function alignRoofSelections(
    layouts: PreContractRoofModuleLayout[],
    form: PreContractIntakeForm,
): boolean[] {
    return layouts.map((layout, index) => roofIsActiveInForm(form, layout, index));
}

function restrictMaxfillToSelection(
    maxfill: PciMaxfillSnapshot,
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): PciMaxfillSnapshot {
    const selectedIds = knownModuleIdMap(layouts, roofSelections);
    const modules = maxfill.modules.filter((row) =>
        selectedIds.has(String(row.id).trim().toUpperCase()),
    );
    const saSum = modules.reduce((sum, row) => sum + row.solarAccess, 0);
    const kwhSum = modules.reduce((sum, row) => sum + (row.productionKwh || 0), 0);
    const ratio = maxfill.moduleCount > 0 ? modules.length / maxfill.moduleCount : 0;
    return {
        ...maxfill,
        modules,
        moduleCount: modules.length,
        annualKwh: kwhSum > 0 ? Math.round(kwhSum) : 0,
        averageSolarAccess: modules.length > 0 ? Math.round(saSum / modules.length) : 0,
        systemKw:
            maxfill.moduleCount > 0
                ? Math.round(maxfill.systemKw * ratio * 1000) / 1000
                : 0,
    };
}

function applyMaxfillProduction(
    layouts: PreContractRoofModuleLayout[],
    maxfill: PciMaxfillSnapshot,
): PreContractRoofModuleLayout[] {
    const byId = new Map<string, PciMaxfillSnapshot['modules'][number]>();
    maxfill.modules.forEach((row) => {
        byId.set(String(row.id).trim().toUpperCase(), row);
    });
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            const row = byId.get(String(cell.id).trim().toUpperCase());
            if (!row) return cell;
            return {
                ...cell,
                solarAccess: row.solarAccess > 0 ? row.solarAccess : cell.solarAccess,
                ...(row.multiplier > 0 ? { multiplier: row.multiplier } : {}),
                ...(row.productionKwh > 0 ? { productionKwh: row.productionKwh } : {}),
            };
        }),
    }));
}

async function designLayoutsWithGemini(
    form: PreContractIntakeForm,
    snapshots: PciMaxfillSnapshot[],
): Promise<GeminiPick[] | null> {
    const apiKey = import.meta.env?.VITE_GEMINI_API_KEY as string | undefined;
    const usable = snapshots.filter((row) => row.modules.length > 0);
    if (!apiKey || usable.length === 0) return null;

    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const targetKwh = usageKwh;
    const payload = {
        targetKwh: targetKwh || null,
        annualUsageKwh: usageKwh || null,
        mustCoverUsage: form.offsetPreference === 'full',
        offsetPreference: form.offsetPreference,
        grayPanelsRequired: form.includeGreyPanels && form.includePurplePanels ? 'yes' : 'no',
        includeGreyPanels: form.includeGreyPanels,
        includePurplePanels: form.includePurplePanels,
        moduleTypeName: form.moduleTypeName || null,
        moduleWattage: form.moduleTypeWattage,
        strategies: usable.map((maxfill) => ({
            orientation: maxfill.orientation ?? 'portrait',
            annualKwh: maxfill.annualKwh,
            averageSolarAccess: maxfill.averageSolarAccess,
            moduleCount: maxfill.moduleCount,
            systemKw: maxfill.systemKw,
            efficiencyTsrf: maxfill.efficiency,
            modules: maxfill.modules.map((row) => ({
                id: row.id,
                roof: row.roofLabel,
                sa: row.solarAccess,
                multiplier: Math.round(row.multiplier * 1000) / 1000,
                kwh: row.productionKwh,
            })),
        })),
    };

    const prompt = `You are a rooftop solar designer. You receive SELECTED roofs and SELECTED modules only (inactive roofs and excluded modules are omitted). Every layout MUST keep this same roof geometry and panel placement — only which of these selected modules are included may change. Each module has REAL solar access (sa 0-100), production multiplier, and estimated annual kWh.

Design up to ${MAX_OPTIONS} distinct layouts. TARGET kWh is always annualUsageKwh (100% of usage). Never aim at a reduced percentage such as 88%.
Rules:
- Use only module ids from this input. Do not invent ids. Do not move, rotate, or mix modules from another fill.
- Keep the same roofs and the same panel positions in every layout. Layouts differ only by which of those modules are included.
- Set "orientation" to this strategy's orientation ("portrait", "landscape", or "both").
- Do not place modules on inactive roofs or use excluded/disabled modules.
- Never include a roof with fewer than 2 modules.
- Prefer higher sa and higher multiplier modules first.
- offsetPreference "full" = COVER annual usage. Keep adding modules until estimated kWh is at or above annualUsageKwh. Modest overshoot is OK and preferred over falling short. Include extra roofs or lower-SA modules if that is what it takes to cover usage. Only return a layout below annualUsageKwh if every eligible module is already included.
- offsetPreference "balanced" = still try to reach as CLOSE as possible to annualUsageKwh, but be SMART about how you get there. Think like a designer: use the fewest roofs and the highest SA/multiplier modules that can get near the target; concentrate on the best roof faces instead of spreading weakly across the house; skip gray/low-SA modules unless high-SA modules cannot get close; do not keep filling leftover roofs after you are already close to usage. Slightly under the target with excellent modules is better than overshooting with poor modules. Closest match with a clean, efficient array is the goal — not a smaller 88% goal.
- includeGreyPanels false = do not include modules with sa from ${PRE_CONTRACT_GREY_SA_MIN} to ${PRE_CONTRACT_LOW_EFFICIENCY_SA} (grey panels).
- includePurplePanels false = do not include modules with sa < ${PRE_CONTRACT_PURPLE_SA_MAX} (purple panels).
- When includeGreyPanels or includePurplePanels is true, those shaded modules are allowed if they help hit the target. For full offset, include them when higher-SA modules are not enough to cover usage. For balanced, only include them if they clearly get you closer to usage without wrecking average SA.
- Layouts must be different from each other (different included module sets).
- Estimated layout kWh is the sum of included modules' kwh (or wattage/1000*multiplier*annual if kwh is 0).

Return JSON only:
{"layouts":[{"id":"usage-fit","orientation":"portrait","title":"short name","reason":"one sentence","moduleIds":["R1M1"]}]}
Best layout first. 1-3 layouts.

Input:
${JSON.stringify(payload)}`;

    const work = callGemini(prompt, GEMINI_MODEL_ID, apiKey, { responseMimeType: 'application/json' });
    const timeout = new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), GEMINI_DESIGN_TIMEOUT_MS);
    });

    try {
        const result = await Promise.race([work, timeout]);
        if (typeof result !== 'string') return null;
        const picks = parseGeminiPicks(result);
        return picks.length > 0 ? picks.slice(0, MAX_OPTIONS) : null;
    } catch {
        return null;
    }
}

function finalizeOptions(ordered: RankedCandidate[], picks: GeminiPick[] | null): PreContractAiDesignOption[] {
    const byId = new Map(ordered.map((c) => [c.id, c]));
    const chosen: RankedCandidate[] = [];

    if (picks) {
        picks.forEach((pick) => {
            const match = byId.get(pick.id);
            if (!match || chosen.some((c) => c.id === match.id)) return;
            chosen.push({
                ...match,
                title: pick.title?.trim() || match.title,
                reason: pick.reason?.trim() || match.reason,
            });
        });
    }

    ordered.forEach((candidate) => {
        if (chosen.length >= MAX_OPTIONS) return;
        if (chosen.some((c) => c.id === candidate.id)) return;
        chosen.push(candidate);
    });

    return chosen.slice(0, MAX_OPTIONS).map((option, index) => ({
        id: option.id,
        rank: index + 1,
        title: option.title,
        reason: option.reason,
        layouts: option.layouts,
        roofSelections: option.roofSelections,
        moduleCount: option.moduleCount,
        systemKw: option.systemKw,
        annualKwh: option.annualKwh,
        utilityKwh: option.utilityKwh ?? null,
        averageSolarAccess: option.averageSolarAccess,
        efficiencyTsrf: option.efficiencyTsrf ?? null,
        multiplier: option.multiplier ?? null,
        offsetPercent: option.offsetPercent,
        roofLabels: option.roofLabels,
        includesGrayPanels: option.includesGrayPanels,
        ...(option.fillOrientation ? { fillOrientation: option.fillOrientation } : {}),
        ...(option.modulesLayout?.length ? { modulesLayout: option.modulesLayout } : {}),
    }));
}

/** Standing tree with the highest production multiplier — the Tree Cutting tab target. */
export function pickHighestMultiplierTree(
    trees: PreContractSiteTree[],
): PreContractSiteTree | null {
    const standing = trees.filter(
        (tree) => !tree.ghosted && Number(tree.multiplier) > 0,
    );
    if (standing.length === 0) return null;
    return [...standing].sort((a, b) => {
        const delta = (Number(b.multiplier) || 0) - (Number(a.multiplier) || 0);
        if (delta !== 0) return delta;
        return String(a.label || a.id).localeCompare(String(b.label || b.id));
    })[0];
}

async function estimateTreeCutCost(tree: PreContractSiteTree): Promise<number | null> {
    try {
        const token = await treeCartService.getExcelServiceToken();
        if (typeof token !== 'string' || !token) return null;
        const height = Number(tree.heightMeters);
        return getTreeCutCostWithDefaults(token, {
            userData: {
                treeId: tree.id,
                height: Number.isFinite(height) && height > 0 ? height : 0,
                trunk_radius: tree.trunkRadiusMeters,
            },
        });
    } catch {
        return null;
    }
}

function uniqueTreeIds(ids: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    ids.forEach((id) => {
        const next = String(id).trim();
        if (!next || seen.has(next)) return;
        seen.add(next);
        out.push(next);
    });
    return out;
}

function selectedModuleKeysFromForm(form: PreContractIntakeForm): Set<string> {
    const keys = new Set<string>();
    form.roofModuleLayouts.forEach((layout) => {
        if (form.roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            cellMatchKeys(cell).forEach((key) => keys.add(key));
        });
    });
    return keys;
}

function layoutsOverlapSelection(
    layouts: PreContractRoofModuleLayout[],
    selectedKeys: Set<string>,
): boolean {
    if (selectedKeys.size === 0) return false;
    return layouts.some((layout) =>
        layout.modules.some((cell) => cell && cellMatchKeys(cell).some((key) => selectedKeys.has(key))),
    );
}

function applySelectionToLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    selectedKeys: Set<string>,
): PreContractRoofModuleLayout[] {
    return layouts.map((layout, index) => {
        const active = roofSelections[index] !== false && roofSelections[layout.roofIndex] !== false;
        return {
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell) return null;
                if (!active) return { ...cell, included: false };
                return {
                    ...cell,
                    included: cellMatchKeys(cell).some((key) => selectedKeys.has(key)),
                };
            }),
        };
    });
}

function applyDefaultIncludedToLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
): PreContractRoofModuleLayout[] {
    return layouts.map((layout, index) => {
        const active = roofSelections[index] !== false && roofSelections[layout.roofIndex] !== false;
        return {
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell) return null;
                if (!active) return { ...cell, included: false };
                return {
                    ...cell,
                    included: isPreContractPanelAllowed(
                        cell.solarAccess,
                        form.includeGreyPanels,
                        form.includePurplePanels,
                    ),
                };
            }),
        };
    });
}

function prepareSnapshotForm(
    form: PreContractIntakeForm,
    snapshot: PciMaxfillSnapshot,
): { form: PreContractIntakeForm; activeMaxfill: PciMaxfillSnapshot | null } {
    const snapshotLayouts =
        snapshot.layouts?.length && snapshot.layouts.some((layout) => layout.modules.some(Boolean))
            ? snapshot.layouts
            : null;
    const selectedKeys = selectedModuleKeysFromForm(form);
    const baseLayouts = snapshotLayouts ?? form.roofModuleLayouts;
    const roofSelections = alignRoofSelections(baseLayouts, form);
    let layouts: PreContractRoofModuleLayout[];
    if (snapshotLayouts) {
        const overlap =
            selectedKeys.size > 0 && layoutsOverlapSelection(snapshotLayouts, selectedKeys);
        layouts = overlap
            ? applySelectionToLayouts(snapshotLayouts, roofSelections, selectedKeys)
            : applyDefaultIncludedToLayouts(snapshotLayouts, roofSelections, form);
        const layoutOrientation =
            snapshot.orientation === 'landscape' || snapshot.orientation === 'portrait'
                ? snapshot.orientation
                : undefined;
        if (layoutOrientation) {
            layouts = layouts.map((layout) => ({
                ...layout,
                moduleOrientation: layoutOrientation,
            }));
        }
    } else {
        layouts = form.roofModuleLayouts;
    }
    const activeMaxfill = snapshotLayouts
        ? restrictMaxfillToSelection(snapshot, layouts, roofSelections)
        : null;
    const layoutsWithProduction =
        activeMaxfill != null
            ? applyMaxfillProduction(layouts, activeMaxfill)
            : layouts;
    return {
        form: {
            ...form,
            roofModuleLayouts: layoutsWithProduction,
            roofSelections,
        },
        activeMaxfill,
    };
}

type PreparedMaxfill = {
    snapshot: PciMaxfillSnapshot;
    form: PreContractIntakeForm;
};

function presentModuleIdSet(layouts: PreContractRoofModuleLayout[]): Set<string> {
    const ids = new Set<string>();
    collectPresentCells(layouts).forEach(({ cell }) => ids.add(cell.id));
    return ids;
}

/** Prefer the max-fill that matches the live roof/panel layout already on the previous screen. */
function pickCanonicalPrepared(
    prepared: PreparedMaxfill[],
    intakeForm: PreContractIntakeForm,
): PreparedMaxfill | null {
    if (prepared.length === 0) return null;
    if (prepared.length === 1) return prepared[0];
    const liveIds = presentModuleIdSet(intakeForm.roofModuleLayouts);
    const liveOrientation = intakeForm.roofModuleLayouts.find(
        (layout) => layout.moduleOrientation,
    )?.moduleOrientation;
    let best = prepared[0];
    let bestScore = Number.NEGATIVE_INFINITY;
    prepared.forEach((row) => {
        const ids = presentModuleIdSet(row.form.roofModuleLayouts);
        let overlap = 0;
        ids.forEach((id) => {
            if (liveIds.has(id)) overlap += 1;
        });
        const orientationBonus =
            liveOrientation && row.snapshot.orientation === liveOrientation ? 500 : 0;
        const score = overlap * 10 + orientationBonus + (row.snapshot.moduleCount || ids.size);
        if (score > bestScore) {
            bestScore = score;
            best = row;
        }
    });
    return best;
}

/** One orientation for max-fill — avoids running portrait, landscape, and both sequentially. */
function pickPrimaryFillOrientation(form: PreContractIntakeForm): PreContractFillOrientation {
    const selected = normalizeFillOrientations(form.fillOrientations);
    if (selected.length === 1) return selected[0];
    const liveOrientation = form.roofModuleLayouts.find(
        (layout) => layout.moduleOrientation,
    )?.moduleOrientation;
    if (liveOrientation && selected.includes(liveOrientation)) return liveOrientation;
    if (selected.includes('both')) return 'both';
    return selected[0];
}

/** Best single layout from intake settings (no Gemini). */
function pickSingleHeuristicCandidate(
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate | null {
    const candidates = collectHeuristicCandidates(form, fillOrientation);
    if (candidates.length === 0) return null;
    const pick = (strategy: CandidateStrategy) =>
        candidates.find((candidate) => candidate.strategy === strategy);
    let chosen: RankedCandidate | null;
    if (form.offsetPreference === 'full') {
        chosen = pick('usage-fit') ?? pick('max-production') ?? candidates[0];
    } else {
        chosen = pick('usage-fit') ?? pick('best-roofs') ?? candidates[0];
    }
    if (!chosen || form.offsetPreference !== 'full') return chosen;

    const includedIds = new Set<string>();
    chosen.layouts.forEach((layout) => {
        if (chosen!.roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) includedIds.add(cell.id);
        });
    });
    const expandedIds = expandIdsToCoverUsage(form, includedIds);
    if (expandedIds.size === includedIds.size) return chosen;
    const expanded = candidateFromIds(chosen.id, expandedIds, form, fillOrientation);
    return expanded ?? chosen;
}

/** Generate one recommended design from the current intake configuration. */
export async function generatePreContractAiDesigns(
    form: PreContractIntakeForm,
    options?: { onProgress?: (stage: PreContractAiProgress) => void },
): Promise<PreContractAiDesignsResult> {
    const empty: PreContractAiDesignsResult = { layoutOptions: [], treeCutOptions: [] };
    const baselineGhostedIds = uniqueTreeIds(
        form.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id),
    );
    const primaryOrientation = pickPrimaryFillOrientation(form);

    let workingForm = form;
    let modulesLayout: SavedRoofModulesLayout | undefined;
    const fills: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>> = {};

    // Always max-fill the configured orientation so module pool matches Run AI settings.
    options?.onProgress?.('maxfill');
    const snapshots = await requestPciMaxfillStrategiesFromEmbed([primaryOrientation]);
    const snapshot =
        snapshots.find(
            (row) =>
                row.layouts?.some((layout) => layout.modules.some(Boolean)) || row.modules.length > 0,
        ) ?? null;
    if (snapshot) {
        const prepared = prepareSnapshotForm(form, {
            ...snapshot,
            orientation: snapshot.orientation ?? primaryOrientation,
        });
        workingForm = prepared.form;
        modulesLayout = snapshot.modulesLayout;
        if (primaryOrientation && snapshot.modulesLayout?.length) {
            fills[primaryOrientation] = snapshot.modulesLayout;
        }
    }

    options?.onProgress?.('layout');
    const ranked = collectHeuristicCandidates(workingForm, primaryOrientation);
    let shortlist: RankedCandidate[] = ranked.slice(0, MAX_OPTIONS).map((draft) => ({
        ...draft,
        fillOrientation: draft.fillOrientation ?? primaryOrientation,
        modulesLayout: draft.modulesLayout ?? modulesLayout,
    }));

    if (shortlist.length === 0) {
        const draft = pickSingleHeuristicCandidate(workingForm, primaryOrientation);
        if (!draft) return empty;
        shortlist = [
            {
                ...draft,
                fillOrientation: draft.fillOrientation ?? primaryOrientation,
                modulesLayout: draft.modulesLayout ?? modulesLayout,
            },
        ];
    }

    options?.onProgress?.('simulate');
    const simulated = await simulateCandidatesInEmbed(shortlist, workingForm, fills);
    const layoutOptions = finalizeOptions(simulated, null).map((option) => ({
        ...option,
        ghostedTreeIds: baselineGhostedIds,
    }));

    return { layoutOptions, treeCutOptions: [] };
}

/** Apply a ranked option onto the intake form. */
export function intakePatchFromAiDesign(
    option: PreContractAiDesignOption,
    siteTrees?: PreContractSiteTree[],
): Pick<PreContractIntakeForm, 'roofModuleLayouts' | 'roofSelections' | 'systemUsageKwh'> &
    Partial<Pick<PreContractIntakeForm, 'siteTrees'>> {
    const patch: Pick<PreContractIntakeForm, 'roofModuleLayouts' | 'roofSelections' | 'systemUsageKwh'> &
        Partial<Pick<PreContractIntakeForm, 'siteTrees'>> = {
        roofModuleLayouts: option.layouts,
        roofSelections: option.roofSelections,
        systemUsageKwh: option.annualKwh > 0 ? String(option.annualKwh) : '',
    };
    if (!siteTrees) return patch;
    const ghosted = new Set(option.ghostedTreeIds ?? []);
    patch.siteTrees = siteTrees.map((tree) => ({
        ...tree,
        ghosted: ghosted.has(tree.id),
    }));
    return patch;
}

const AI_OPTIONS_CACHE_KEY = 'preContractAiDesignOptionsCache.v13';

type PreContractAiOptionsCache = {
    layoutOptions: PreContractAiDesignOption[];
    treeCutOptions: PreContractAiDesignOption[];
    selectedId: string | null;
    selectedTab?: 'layouts' | 'trees';
};

/** Cache ranked options so returning from OneDraw does not regenerate them. */
export function persistPreContractAiOptionsCache(
    layoutOptions: PreContractAiDesignOption[],
    treeCutOptions: PreContractAiDesignOption[],
    selectedId: string | null,
    selectedTab: 'layouts' | 'trees' = 'layouts',
): void {
    try {
        const payload: PreContractAiOptionsCache = {
            layoutOptions,
            treeCutOptions,
            selectedId,
            selectedTab,
        };
        sessionStorage.setItem(AI_OPTIONS_CACHE_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function loadPreContractAiOptionsCache(): PreContractAiOptionsCache | null {
    try {
        const raw = sessionStorage.getItem(AI_OPTIONS_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PreContractAiOptionsCache> & {
            options?: PreContractAiDesignOption[];
        };
        const layoutOptions = Array.isArray(parsed.layoutOptions)
            ? parsed.layoutOptions
            : Array.isArray(parsed.options)
              ? parsed.options
              : [];
        if (layoutOptions.length === 0) return null;
        return {
            layoutOptions,
            treeCutOptions: Array.isArray(parsed.treeCutOptions) ? parsed.treeCutOptions : [],
            selectedId:
                typeof parsed.selectedId === 'string'
                    ? parsed.selectedId
                    : layoutOptions[0]?.id ?? null,
            selectedTab: parsed.selectedTab === 'trees' ? 'trees' : 'layouts',
        };
    } catch {
        return null;
    }
}

export function clearPreContractAiOptionsCache(): void {
    try {
        sessionStorage.removeItem(AI_OPTIONS_CACHE_KEY);
    } catch {
        /* ignore */
    }
}

/** True when a prior Run AI result is still cached for this session. */
export function hasPreContractAiOptionsCache(): boolean {
    return loadPreContractAiOptionsCache() != null;
}

/** Currently selected AI option from the session cache (layouts or tree-cut tab). */
export function resolveSelectedPreContractAiOption(): PreContractAiDesignOption | null {
    const cached = loadPreContractAiOptionsCache();
    if (!cached) return null;
    const tab = cached.selectedTab === 'trees' && cached.treeCutOptions.length > 0 ? 'trees' : 'layouts';
    const pool = tab === 'trees' ? cached.treeCutOptions : cached.layoutOptions;
    return pool.find((item) => item.id === cached.selectedId) ?? pool[0] ?? null;
}
